import type { InfiniteData, UseSuspenseInfiniteQueryOptions } from '@tanstack/react-query';
import { useSuspenseInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query';
import type { CreativeText } from '../types';
import queryKeys from './queryKeys';
import type { QueryConfig } from 'lib/react-query';
import { apiClient } from 'lib/api';
import type { ListResponse, SortingParams } from 'types/shared';
import queryString from 'query-string';

interface GetCreativeTextsParams extends Partial<SortingParams> {
  name?: string;
  apps?: string;
  limit?: number;
  offset?: number;
}

export const getCreativeTexts = async (
  params?: GetCreativeTextsParams,
  signal?: AbortSignal,
): Promise<ListResponse<CreativeText>> => {
  const res = await apiClient.get<ListResponse<CreativeText>>(
    queryKeys.creativeTexts,
    {
      params,
      signal,
      paramsSerializer: (prms) =>
        queryString.stringify(prms, { arrayFormat: 'comma' }),
    },
  );
  return res.data;
};

interface UseCreativeTextsOptions<TData> {
  config?: QueryConfig<typeof getCreativeTexts, TData>;
  params?: GetCreativeTextsParams;
}

export const useCreativeTexts = <TData = ListResponse<CreativeText>>
({
   params,
   config,
 }: UseCreativeTextsOptions<TData> = {}) => {
  return useSuspenseQuery({
    staleTime: 60 * 30 * 1000,
    ...config,
    queryFn: ({ signal }) => getCreativeTexts(params, signal),
    queryKey: [queryKeys.creativeTexts, params],
  });
};

interface UseInfiniteCreativeTextsOptions {
  config?: Omit<
    UseSuspenseInfiniteQueryOptions<
      ListResponse<CreativeText>,
      unknown,
      InfiniteData<ListResponse<CreativeText>>,
      readonly unknown[],
      number
    >,
    'queryFn' | 'queryKey' | 'initialPageParam' | 'getNextPageParam'
  >;
  params?: GetCreativeTextsParams;
}

export const useInfiniteCreativeTexts =
  ({
     config,
     params,
   }: UseInfiniteCreativeTextsOptions = {}) => {
    return useSuspenseInfiniteQuery({
      ...config,
      queryFn: ({ pageParam = 1, signal }) =>
        getCreativeTexts(
          {
            ...params,
            offset: pageParam,
            limit: params?.limit ?? 100,
          },
          signal,
        ),
      queryKey: [
        queryKeys.creativeTexts,
        'infinite',
        ...Object.values(params ?? {}),
      ],
      getNextPageParam: (lastPage, allPages) => {
        const totalItems = lastPage.count;
        const loadedItems = allPages.reduce(
          (acc, page) => acc + page.data.length,
          0,
        );

        if (loadedItems >= totalItems) {
          return undefined;
        }

        return allPages.length + 1;
      },
      initialPageParam: 1,
    });
  };
